/**
 * DateRangePicker — 날짜 범위 선택기 (빠른 검색 프리셋 + 범위 + 헤더 드릴다운)
 *
 * 팝오버 = 좌측 "빠른 검색" 프리셋 리스트 + 우측 캘린더(CalendarCore, range 모드).
 * 캘린더에서 시작~종료를 2-클릭으로 고르고(역순 클릭 시 자동 스왑),
 * 두 날짜가 확정되면 onChange(range, { presetKey })가 발화한다.
 * 프리셋 클릭은 즉시 범위를 계산해 onChange.
 *
 * 값 포맷은 YYYY-MM-DD 문자열 — 기존 페이지의 URL(from/to) 규약과 동일.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from './cn'
import { CalendarCore } from './CalendarCore'
import { toDate, formatISO } from './DatePicker'
import { DEFAULT_RANGE_PRESETS, type DateRangePreset } from '../lib/dateRange'
import styles from './DateRangePicker.module.css'

export interface DateRange {
  from: string | null
  to: string | null
}

export interface DateRangePickerProps {
  /** 선택된 범위 (YYYY-MM-DD). null 이면 미선택 */
  value?: DateRange | null
  /**
   * 범위 확정 시 호출 (from<=to 정규화 후).
   * presetKey 는 프리셋으로 골랐을 때만 non-null, 캘린더 직접 선택이면 null.
   */
  onChange?: (range: DateRange, meta: { presetKey: string | null }) => void
  /** 빠른 검색 프리셋. 미지정 시 기본 5종(오늘/7일/14일/30일/이번달) */
  presets?: DateRangePreset[]
  /** 하이라이트할 프리셋 키 (제어형). 미지정 시 value 매칭으로 추론 */
  activePresetKey?: string | null
  /** 최소 선택 가능 날짜 */
  minDate?: Date
  /** 최대 선택 가능 날짜 */
  maxDate?: Date
  /** 입력 크기 */
  size?: 'sm' | 'md'
  /** 비활성 상태 */
  disabled?: boolean
  /** 캘린더를 in-flow로 렌더 — 부모가 overflow:hidden일 때 */
  inline?: boolean
  /** 필드 레이블 */
  label?: string
  /** 플레이스홀더 */
  placeholder?: string
  /** 추가 클래스명 */
  className?: string
}

/** 'YYYY-MM-DD' → '2026.06.01' 표시용 */
function formatDisplay(ymd: string): string {
  return ymd.replace(/-/g, '.')
}

export function DateRangePicker({
  value,
  onChange,
  presets = DEFAULT_RANGE_PRESETS,
  activePresetKey,
  minDate,
  maxDate,
  size = 'md',
  disabled = false,
  inline = false,
  label,
  placeholder = '기간을 선택하세요',
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // 외부 value → 안정적 Date 참조 (timestamp 메모이즈)
  const valueFromTime = (value?.from ? toDate(value.from) : null)?.getTime() ?? null
  const valueToTime = (value?.to ? toDate(value.to) : null)?.getTime() ?? null

  // 캘린더 2-클릭 진행 상태 (draft). 팝오버가 열려있는 동안만 유효.
  const [draftStart, setDraftStart] = useState<Date | null>(null)
  const [draftEnd, setDraftEnd] = useState<Date | null>(null)
  const [hoverDate, setHoverDate] = useState<Date | null>(null)

  // 팝오버 열릴 때 현재 value로 draft 초기화
  useEffect(() => {
    if (open) {
      setDraftStart(valueFromTime != null ? new Date(valueFromTime) : null)
      setDraftEnd(valueToTime != null ? new Date(valueToTime) : null)
      setHoverDate(null)
    }
  }, [open, valueFromTime, valueToTime])

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((v) => !v)
  }, [disabled])

  // 외부 클릭 / ESC 닫기
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const commit = useCallback((from: Date, to: Date, presetKey: string | null) => {
    // lo<=hi 정규화
    const [lo, hi] = from <= to ? [from, to] : [to, from]
    onChange?.({ from: formatISO(lo), to: formatISO(hi) }, { presetKey })
    setOpen(false)
  }, [onChange])

  const handleSelectDay = useCallback((d: Date) => {
    // 시작 미확정이거나 이미 범위 완성 상태 → 새 시작
    if (!draftStart || draftEnd) {
      setDraftStart(d)
      setDraftEnd(null)
      setHoverDate(null)
      return
    }
    // 시작만 있고 종료 선택 → 확정
    commit(draftStart, d, null)
  }, [draftStart, draftEnd, commit])

  const handlePreset = useCallback((preset: DateRangePreset) => {
    const { from, to } = preset.getRange()
    onChange?.({ from, to }, { presetKey: preset.key })
    setOpen(false)
  }, [onChange])

  // 프리셋 하이라이트: activePresetKey 우선, 없으면 value 매칭 추론
  const resolvedActiveKey = useMemo(() => {
    if (activePresetKey !== undefined) return activePresetKey
    if (!value?.from || !value?.to) return null
    const matched = presets.find((p) => {
      const r = p.getRange()
      return r.from === value.from && r.to === value.to
    })
    return matched?.key ?? null
  }, [activePresetKey, value?.from, value?.to, presets])

  // 트리거 표시 텍스트
  const triggerText = value?.from && value?.to
    ? `${formatDisplay(value.from)} ~ ${formatDisplay(value.to)}`
    : null

  // 캘린더 포커스: 진행 중 시작일 > 확정 시작일 > 오늘
  const focusDate = draftStart ?? (valueFromTime != null ? new Date(valueFromTime) : null)

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <button
        type="button"
        className={cn(styles.trigger, styles[size], disabled && styles.disabled)}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cn(styles.triggerText, !triggerText && styles.placeholder)}>
          {triggerText ?? placeholder}
        </span>
        <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={cn(styles.popover, inline && styles.popoverInline)}>
          {presets.length > 0 && (
            <div className={styles.presetList}>
              <span className={styles.presetTitle}>빠른 검색</span>
              {presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={cn(styles.presetBtn, resolvedActiveKey === p.key && styles.presetActive)}
                  onClick={() => handlePreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <div className={styles.calendarArea}>
            <CalendarCore
              mode="range"
              rangeStart={draftStart}
              rangeEnd={draftEnd}
              hoverDate={hoverDate}
              onHoverDate={setHoverDate}
              focusDate={focusDate}
              minDate={minDate}
              maxDate={maxDate}
              onSelectDay={handleSelectDay}
            />
          </div>
        </div>
      )}
    </div>
  )
}
